import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import RegularLoader from "../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../config/api";

export default function BadgeForm({ badge = null, onSuccess, onClose }) {
  const [loading, setLoading] = useState(false);

  /* ---------------- INITIAL STATE ---------------- */
  const initialFormState = {
    name: "",
    eligibility: "",
    description: "",
    benefits: "",
    payment_type: "",
    pay_now: 0,
    pay_later: 0,
    image: null,
  };

  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  /* ---------------- PREFILL (EDIT MODE) ---------------- */
 useEffect(() => {
  if (badge) {
    const isPayNow = Number(badge.pay_now) === 1;
    const isPayLater = Number(badge.pay_later) === 1;

    setForm({
      name: badge.name || "",
      eligibility: badge.eligibility || "",
      description: badge.description || "",
      benefits: badge.benefits || "",
      payment_type: isPayNow ? "pay_now" : isPayLater ? "pay_later" : "",
      pay_now: isPayNow ? 1 : 0,
      pay_later: isPayLater ? 1 : 0,
      image: null,
    });
  }
}, [badge]);


  /* ---------------- INPUT HANDLER ---------------- */
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "payment_type") {
      setForm((prev) => ({
        ...prev,
        payment_type: value,
        pay_now: value === "pay_now" ? 1 : 0,
        pay_later: value === "pay_later" ? 1 : 0,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: files ? files[0] : value,
      }));
    }

    // clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /* ---------------- VALIDATION ---------------- */
  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Badge name is required";
    if (!form.eligibility.toString().trim())
      newErrors.eligibility = "Eligibility is required";
    if (!form.benefits.toString().trim())
      newErrors.benefits = "Benefits is required";
    else if (Number(form.benefits) <= 0)
      newErrors.benefits = "Benefits must be greater than 0";

    if (!form.payment_type)
      newErrors.payment_type = "Payment type is required";

    if (!form.description.trim())
      newErrors.description = "Description is required";

    if (!badge && !form.image)
      newErrors.image = "Badge image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  setLoading(true);

  const toastId = toast.loading(
    badge ? "Updating badge..." : "Creating badge..."
  );

  const formData = new FormData();
  formData.append("name", form.name);
  formData.append("eligibility", form.eligibility);
  formData.append("description", form.description);
  formData.append("benefits", form.benefits);
  formData.append("pay_now", form.pay_now);
  formData.append("pay_later", form.pay_later);
  if (form.image) formData.append("image", form.image);

  try {
    const response = await fetch(
      badge ? `${BASE_URL}badges/${badge.id}` : `${BASE_URL}badges`,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      toast.error(data?.message || "Failed to save badge", { id: toastId });
      return;
    }

    toast.success(
      badge ? "Badge updated successfully" : "Badge created successfully",
      { id: toastId }
    );
    window.location.reload()

    setForm(initialFormState);
    setErrors({});
    onSuccess?.(data);
    onClose?.();
  } catch (error) {
    toast.error("Server error. Please try again.", { id: toastId });
  } finally {
    setLoading(false);
  }
};


  /* ---------------- UI ---------------- */
  return (
    <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 w-full md:w-[400px] relative">
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-slate-500 hover:text-slate-700"
      >
        <X size={20} />
      </button>

      <h2 className="font-semibold text-slate-900 mb-3">
        {badge ? "Update Badge" : "Create Badge"}
      </h2>

      <form onSubmit={handleSubmit} className="grid gap-4 text-sm">
        {/* Name */}
        <Field
          label="Badge Name"
          error={errors.name}
          input={
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
               className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs md:text-sm"
            />
          }
        />

        {/* Eligibility */}
        <Field
          label="Eligibility Users"
          error={errors.eligibility}
          input={
            <input
              name="eligibility"
              value={form.eligibility}
              onChange={handleChange}
               className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs md:text-sm"
            />
          }
        />

        {/* Benefits */}
        <Field
          label="Benefits (%)"
          error={errors.benefits}
          input={
            <input
              type="number"
              name="benefits"
              value={form.benefits}
              onChange={handleChange}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs md:text-sm"
            />
          }
        />

        {/* Payment Type */}
        <Field
          label="Payment Type"
          error={errors.payment_type}
          input={
            <select
              name="payment_type"
              value={form.payment_type}
              onChange={handleChange}
               className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs md:text-sm"
            >
              <option value="">Select</option>
              <option value="pay_now">Pay Now</option>
              <option value="pay_later">Pay Later</option>
            </select>
          }
        />

        {/* Description */}
        <Field
          label="Description"
          error={errors.description}
          input={
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
               className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs md:text-sm"
            />
          }
        />

        {/* Image */}
        <Field
          label="Badge Image"
          error={errors.image}
          input={
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
            />
          }
        />

        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? "Saving..." : badge ? "Update Badge" : "Create Badge"}
        </button>
      </form>

    </div>
  );
}

/* ---------------- FIELD WRAPPER ---------------- */
/* ---------------- FIELD WRAPPER ---------------- */
function Field({ label, input, error }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-slate-600 text-xs md:text-sm">{label}</label>
      {input}
      {error && <span className="text-red-500 text-xs">{error}</span>}
    </div>
  );
}





// import React, { useState, useEffect } from "react";
// import toast from "react-hot-toast";
// import { X } from "lucide-react";
// import RegularLoader from "../../Component/Loader/RegularLoader";
// import { BASE_URL } from "../../config/api";

// export default function BadgeForm({ badge = null, onSuccess, onClose }) {
//   const [loading, setLoading] = useState(false);

//   /* ---------------- INITIAL STATE ---------------- */
//   const initialFormState = {
//     name: "",
//     eligibility: "",
//     description: "",
//     benefits: "",
//     payment_type: "", // pay_now | pay_later
//     pay_now: 0,
//     pay_later: 0,
//     image: null,
//   };

//   const [form, setForm] = useState(initialFormState);
//   const [errors, setErrors] = useState({});

//   /* ---------------- PREFILL (EDIT MODE) ---------------- */
//   useEffect(() => {
//     if (badge) {
//       setForm({
//         name: badge.name || "",
//         eligibility: badge.eligibility || "",
//         description: badge.description || "",
//         benefits: badge.benefits || "",
//         payment_type:
//           badge.pay_now === 1 ? "pay_now" : badge.pay_later === 1 ? "pay_later" : "",
//         pay_now: badge.pay_now || 0,
//         pay_later: badge.pay_later || 0,
//         image: null,
//       });
//     }
//   }, [badge]);

//   /* ---------------- INPUT HANDLER ---------------- */
//   const handleChange = (e) => {
//     const { name, value, files } = e.target;

//     if (name === "payment_type") {
//       setForm((prev) => ({
//         ...prev,
//         payment_type: value,
//         pay_now: value === "pay_now" ? 1 : 0,
//         pay_later: value === "pay_later" ? 1 : 0,
//       }));
//     } else {
//       setForm((prev) => ({
//         ...prev,
//         [name]: files ? files[0] : value,
//       }));
//     }

//     if (errors[name]) {
//       setErrors((prev) => ({ ...prev, [name]: "" }));
//     }
//   };

//   /* ---------------- VALIDATION ---------------- */
//   const validate = () => {
//     let newErrors = {};

//     if (!form.name.trim()) newErrors.name = "Name is required";
//     if (!form.eligibility.toString().trim())
//       newErrors.eligibility = "Eligibility is required";
//     if (!form.description.trim())
//       newErrors.description = "Description is required";
//     if (!form.benefits || Number(form.benefits) <= 0)
//       newErrors.benefits = "Enter valid benefits";
//     if (!form.payment_type)
//       newErrors.payment_type = "Select payment type";

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   /* ---------------- SUBMIT ---------------- */
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setLoading(true);

//     const formData = new FormData();
//     formData.append("name", form.name);
//     formData.append("eligibility", form.eligibility);
//     formData.append("description", form.description);
//     formData.append("benefits", form.benefits);
//     formData.append("pay_now", form.pay_now);
//     formData.append("pay_later", form.pay_later);
//     if (form.image) formData.append("image", form.image);

//     try {
//       const response = await fetch(
//         badge ? `${BASE_URL}badges/${badge.id}` : `${BASE_URL}badges`,
//         {
//           method: "POST",
//           body: formData,
//         }
//       );

//       const data = await response.json();

//       if (!response.ok) {
//         toast.error(data?.message || "Failed to save badge");
//         return;
//       }

//       toast.success(`Badge ${badge ? "updated" : "created"} successfully`);
//       setForm(initialFormState);
//       setErrors({});
//       onSuccess && onSuccess(data);
//       onClose && onClose();
//     } catch (error) {
//       toast.error("Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- UI ---------------- */
//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 w-full md:w-[400px] relative">
//       <button
//         onClick={onClose}
//         className="absolute top-3 right-3 text-slate-500 hover:text-slate-700"
//       >
//         <X size={20} />
//       </button>

//       <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
//         {badge ? "Update Badge" : "Create Badge"}
//       </h2>

//       <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 text-xs md:text-sm">
//         {/* Badge Name */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Badge Name</label>
//           <input
//             name="name"
//             value={form.name}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//           />
//           {errors.name && <span className="text-red-500">{errors.name}</span>}
//         </div>

//         {/* Eligibility */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Eligibility Users</label>
//           <input
//             name="eligibility"
//             value={form.eligibility}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//           />
//           {errors.eligibility && (
//             <span className="text-red-500">{errors.eligibility}</span>
//           )}
//         </div>

//         {/* Benefits */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Benefits (%)</label>
//           <input
//             name="benefits"
//             type="number"
//             value={form.benefits}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//           />
//           {errors.benefits && (
//             <span className="text-red-500">{errors.benefits}</span>
//           )}
//         </div>

//         {/* Payment Type Dropdown */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Payment Type</label>
//           <select
//             name="payment_type"
//             value={form.payment_type}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//           >
//             <option value="">Select Payment Type</option>
//             <option value="pay_now">Pay Now</option>
//             <option value="pay_later">Pay Later</option>
//           </select>
//           {errors.payment_type && (
//             <span className="text-red-500">{errors.payment_type}</span>
//           )}
//         </div>

//         {/* Description */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Description</label>
//           <textarea
//             name="description"
//             value={form.description}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5 h-20"
//           />
//           {errors.description && (
//             <span className="text-red-500">{errors.description}</span>
//           )}
//         </div>

//         {/* Image */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Upload Badge Image</label>
//           <input type="file" accept="image/*" name="image" onChange={handleChange} />
//         </div>

//         <button
//           type="submit"
//           disabled={loading}
//           className="bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
//         >
//           {loading ? "Saving..." : badge ? "Update Badge" : "Create Badge"}
//         </button>
//       </form>

//       {loading && <RegularLoader />}
//     </div>
//   );
// }



// ****************************************working code********************************************************


// import React, { useState, useEffect } from "react";
// import toast from "react-hot-toast";
// import { X } from "lucide-react";
// import RegularLoader from "../../Component/Loader/RegularLoader";
// import { BASE_URL } from "../../config/api";

// export default function BadgeForm({ badge = null, onSuccess, onClose }) {
//   const [loading, setLoading] = useState(false);

//   const initialFormState = {
//   name: "",
//   eligibility: "",
//   description: "",
//   benefits: "",
//   image: null,
// };

//   const [form, setForm] = useState(initialFormState);


//   const [errors, setErrors] = useState({});

//   // Pre-fill form for update
//   useEffect(() => {
//     if (badge) {
//       setForm({
//         name: badge.name || "",
//         eligibility: badge.eligibility || "",
//         description: badge.description || "",
//         benefits: badge.benefits || "",
//         image: null, // image can be updated separately
//       });
//     }
//   }, [badge]);

//   // Handle input changes
//   const handleChange = (e) => {
//     const { name, value, files } = e.target;

//     setForm((prev) => ({
//       ...prev,
//       [name]: files ? files[0] : value,
//     }));

//     if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
//   };

//   // Validation
//   const validate = () => {
//     let newErrors = {};
//     if (!form.name.trim()) newErrors.name = "Name is required";
//     if (!form.eligibility.toString().trim())
//       newErrors.eligibility = "Eligibility is required";
//     if (!form.description.trim()) newErrors.description = "Description is required";
//     if (!form.benefits.toString().trim() || Number(form.benefits) <= 0)
//       newErrors.benefits = "Enter valid benefits";

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // Form submit
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setLoading(true);

//     const formData = new FormData();
//     formData.append("name", form.name);
//     formData.append("eligibility", form.eligibility);
//     formData.append("description", form.description);
//     formData.append("benefits", form.benefits);
//     if (form.image) formData.append("image", form.image);

//     try {
//       let response;
//       if (badge) {
//         // Update mode
//         response = await fetch(`${BASE_URL}badges/${badge.id}`, {
//           method: "POST", // Use POST with _method=PUT if API requires
//           body: formData,
//         });
//       } else {
//         // Create mode
//         response = await fetch(`${BASE_URL}badges`, {
//           method: "POST",
//           body: formData,
//         });
//       }

//       const data = await response.json();

//       if (!response.ok) {
//         toast.error(data?.message || "Failed to save badge");
//         return;
//       }

//       toast.success(`Badge ${badge ? "updated" : "created"} successfully!`);
//       setForm(initialFormState);
//       setErrors({});
//       onSuccess && onSuccess(data);
//       onClose && onClose();
//     } catch (err) {
//       toast.error("Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 w-full md:w-[400px] relative">
//       {/* Close Button */}
//       <button
//         onClick={onClose}
//         className="absolute top-3 right-3 text-slate-500 hover:text-slate-700"
//       >
//         <X size={20} />
//       </button>

//       <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
//         {badge ? "Update Badge" : "Create Badge"}
//       </h2>

//       <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 text-xs md:text-sm">
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Badge Name</label>
//           <input
//             name="name"
//             value={form.name}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Enter badge name"
//           />
//           {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
//         </div>

//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Eligibility Users</label>
//           <input
//             name="eligibility"
//             value={form.eligibility}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Number of users"
//           />
//           {errors.eligibility && (
//             <span className="text-red-500 text-xs">{errors.eligibility}</span>
//           )}
//         </div>

//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Benefits %</label>
//           <input
//             name="benefits"
//             type="number"
//             value={form.benefits}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Example: 10"
//           />
//           {errors.benefits && <span className="text-red-500 text-xs">{errors.benefits}</span>}
//         </div>

//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Description</label>
//           <textarea
//             name="description"
//             value={form.description}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5 h-20"
//             placeholder="Write badge description"
//           />
//           {errors.description && (
//             <span className="text-red-500 text-xs">{errors.description}</span>
//           )}
//         </div>

//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Upload Badge Image</label>
//           <input type="file" accept="image/*" name="image" onChange={handleChange} />
//           {errors.image && <span className="text-red-500 text-xs">{errors.image}</span>}
//         </div>

//         <button
//           type="submit"
//           disabled={loading}
//           className="bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-sm"
//         >
//           {loading ? "Saving..." : badge ? "Update Badge" : "Create Badge"}
//         </button>
//       </form>
//     </div>
//   );
// }




// import React, { useState } from "react";
// import toast from "react-hot-toast";
// import RegularLoader from "../../Component/Loader/RegularLoader";

// export default function CreateBadge({ onCreate }) {
//   const [loading, setLoading] = useState(false);

//   const [form, setForm] = useState({
//     name: "",
//     eligibility: "",
//     description: "",
//     benefits: "",
//     image: null,
//   });

//   const [errors, setErrors] = useState({});

//   // ---- HANDLE INPUT CHANGE ----
//   const handleChange = (e) => {
//     const { name, value, files } = e.target;

//     setForm((prev) => ({
//       ...prev,
//       [name]: files ? files[0] : value, // handle image upload
//     }));

//     if (errors[name]) {
//       setErrors((prev) => ({ ...prev, [name]: "" }));
//     }
//   };

//   // ---- VALIDATION ----
//   const validate = () => {
//     let newErrors = {};

//     if (!form.name.trim()) newErrors.name = "Name is required";
//     if (!form.eligibility.trim()) newErrors.eligibility = "Eligibility is required";
//     if (!form.description.trim()) newErrors.description = "Description is required";
//     if (!form.benefits.trim() || Number(form.benefits) <= 0)
//       newErrors.benefits = "Enter valid benefits";
//     if (!form.image) newErrors.image = "Image is required";

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ---- FORM SUBMIT ----
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setLoading(true);

//     const formData = new FormData();
//     formData.append("name", form.name);
//     formData.append("eligibility", form.eligibility);
//     formData.append("description", form.description);
//     formData.append("benefits", form.benefits);
//     formData.append("image", form.image);

//     try {
//       const response = await fetch("${BASE_URL}/badges", {
//         method: "POST",
//         body: formData,
//       });

//       const data = await response.json();

//       if (!response.ok) {
//         toast.error(data?.message || "Failed to create badge");
//         return;
//       }

//       toast.success("Badge created successfully!");

//       onCreate && onCreate(data);

//       // Reset form
//       setForm({
//         name: "",
//         eligibility: "",
//         description: "",
//         benefits: "",
//         image: null,
//       });

//       setErrors({});
//     } catch (error) {
//       toast.error("Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4">
//       <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
//         Create Badge
//       </h2>

//       <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 text-xs md:text-sm">
        
//         {/* NAME */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Badge Name</label>
//           <input
//             name="name"
//             value={form.name}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Enter badge name"
//           />
//           {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
//         </div>

//         {/* ELIGIBILITY */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Eligibility Users</label>
//           <input
//             name="eligibility"
//             value={form.eligibility}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Number of users"
//           />
//           {errors.eligibility && (
//             <span className="text-red-500 text-xs">{errors.eligibility}</span>
//           )}
//         </div>

//         {/* BENEFITS */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Benefits %</label>
//           <input
//             name="benefits"
//             type="number"
//             value={form.benefits}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Example: 10"
//           />
//           {errors.benefits && (
//             <span className="text-red-500 text-xs">{errors.benefits}</span>
//           )}
//         </div>

//         {/* DESCRIPTION */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Description</label>
//           <textarea
//             name="description"
//             value={form.description}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5 h-20"
//             placeholder="Write badge description"
//           />
//           {errors.description && (
//             <span className="text-red-500 text-xs">{errors.description}</span>
//           )}
//         </div>

//         {/* IMAGE UPLOAD */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Upload Badge Image</label>
//           <input type="file" accept="image/*" name="image" onChange={handleChange} />
//           {errors.image && <span className="text-red-500 text-xs">{errors.image}</span>}
//         </div>

//         {/* BUTTON */}
//         <button
//           type="submit"
//           disabled={loading}
//           className="bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-sm"
//         >
//           {loading ? "Creating...." : "Create Badge"}
//         </button>
//       </form>
//     </div>
//   );
// }




// import React, { useState, useEffect } from "react";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import toast from "react-hot-toast";
// import RegularLoader from "../../Component/Loader/RegularLoader";

// export default function CreateBadge({ onCreate }) {

//   const authUser = JSON.parse(localStorage.getItem("authUserData"));

//   const ROLE_OPTIONS = ["company", "agent", "retailer"];
 


//   // Filter role list based on logged-in user type
//   const filteredRoleOptions = () => {
//     const role = authUser?.role;

//     if (role === 1) return ROLE_OPTIONS;
//     if (role === 2) return ["agent", "retailer"];
//     if (role === 4) return ["retailer"];

//     return [];
//   };

//   const [form, setForm] = useState({
//     name: "",
//     owner_name: "",
//     phone: "",
//     state: "",
//     district: "",
//     pincode: "",
//     email: "",
//     password: 123456,
//     address_full: "",
//     lead_amount: "",
//     status: STATUSES[0]?.value || "new",
//     created_by_id: authUser?.id ?? "",
//     created_by_name: authUser?.business_name ?? "",
//     lead_type:"", 
//   });

//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [pinLoading, setPinLoading] = useState(false);
//   const [packages, setPackages] = useState([]);
// const [packageModal, setPackageModal] = useState(false);
// const [packageLoading, setPackageLoading] = useState(false);


// const fetchPackages = async () => {
//   setPackageModal(true);
//   setPackageLoading(true);

//   try {
//     const res = await fetch("${BASE_URL}/packages");
//     const data = await res.json();
//     setPackages(data);
//   } catch (error) {
//     toast.error("Failed to load packages");
//   } finally {
//     setPackageLoading(false);
//   }
// };

// // ---- SELECT PACKAGE ----
// const handleSelectPackage = (item) => {
//   setForm((prev) => ({
//     ...prev,
//     lead_amount: item.amount,
//     package_id: item.id,
//     package_name: item.package_name,
//     badge_id: item.badge_id || "",
//     badge_name: item.badge?.name || "",
//     benefits: item.badge?.benefits || 0,
//     eligibility: item.badge?.eligibility || "",
//   }));
//   setPackageModal(false);
//   toast.success(`Package "${item.package_name}" selected`);
// };

//   // ---------------- FETCH PINCODE DETAILS ------------------
//   const fetchPincodeDetails = async (pincode) => {
//     if (!/^[0-9]{6}$/.test(pincode)) return;

//     try {
//       setPinLoading(true);

//       const res = await fetch(
//         `https://goelectronix.in/public/api/v1/pincode/${pincode}`
//       );

//       const data = await res.json();

//       if (data.success) {
//         setForm((prev) => ({
//           ...prev,
//           state: data.state || "",
//           district: data.district || "",
//         }));

//         setErrors((prev) => ({
//           ...prev,
//           pincode: "",
//         }));
//       } else {
//         setErrors((prev) => ({
//           ...prev,
//           pincode: "Invalid pincode",
//         }));
//         toast.error("Invalid pincode");
//       }
//     } catch (error) {
//       toast.error("Unable to fetch pincode details");
//     } finally {
//       setPinLoading(false);
//     }
//   };


//   // Auto-assign user type if only one option exists
//   useEffect(() => {
//     const options = filteredRoleOptions();
//     if (options.length === 1) {
//       setForm((prev) => ({ ...prev, lead_type: options[0] }));
//     }
//   }, []);

//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     setForm((prev) => ({
//       ...prev,
//       [name]: value,
//     }));

//     // Pincode auto actions
//     if (name === "pincode") {
//       // If user deletes pincode or less than 6 → clear state & district
//       if (value.length < 6) {
//         setForm((prev) => ({
//           ...prev,
//           state: "",
//           district: "",
//         }));
//         return; // stop here, do not fetch
//       }

//       // When pincode is exactly 6 digits → fetch API
//       if (value.length === 6) {
//         fetchPincodeDetails(value);
//       }
//     }
//   };



//   // ---------------- VALIDATION ------------------
//   const validate = () => {
//     let newErrors = {};

//     if (!form.name.trim()) newErrors.name = "Name is required";
//     else if (form.name.trim().length < 3)
//       newErrors.name = "Name must be at least 3 characters";

//     if (!form.owner_name.trim()) newErrors.owner_name = "Name is required";
//     else if (form.owner_name.trim().length < 3)
//       newErrors.owner_name = "Name must be at least 3 characters";

//     if (!form.phone.trim()) newErrors.phone = "Phone is required";
//     else if (!/^[0-9]{10}$/.test(form.phone))
//       newErrors.phone = "Phone must be 10 digits";

//     if (!form.state.trim()) newErrors.state = "State is required";

//     if (!form.district.trim()) newErrors.district = "District is required";

//     if (!/^[0-9]{6}$/.test(form.pincode))
//       newErrors.pincode = "Pincode must be 6 digits";

//     if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
//       newErrors.email = "Invalid email format";

//     // if (!form.password.trim()) newErrors.password = "Password is required";
//     // else if (form.password.length < 6)
//     //   newErrors.password = "Password must be at least 6 characters";

//     if (!form.address_full.trim())
//       newErrors.address_full = "Address cannot be empty";

//     if (!form.lead_amount || Number(form.lead_amount) <= 0)
//       newErrors.lead_amount = "Enter a valid amount";

//     if (!form.lead_type)
//       newErrors.lead_type = "Please select a lead type";

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ---------------- SUBMIT ------------------
//   const handleSubmit = async (e) => {
//   e.preventDefault();

//   if (!validate()) return;
//   setLoading(true);

//   try {
//     const response = await fetch(
//       "${BASE_URL}/warranty/wlead/store",
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           ...form,
//           lead_amount: Number(form.lead_amount),
//           lead_type: form.lead_type, // 👈 pass selected lead type correctly
//         }),
//       }
//     );

//     const data = await response.json();

//     if (!response.ok) {
//       // Check for validation errors
//       if (data?.errors) {
//         // Convert all field errors into a single message
//         const messages = Object.values(data.errors)
//           .flat() // flatten arrays
//           .join(", "); // join multiple messages

//         toast.error(messages);
//       } else {
//         toast.error(data?.message || "Failed to create lead");
//       }
//       return;
//     }

//     toast.success("Lead created successfully!");
//     onCreate && onCreate(data);

//     // Reset form except auto fields
//     setForm({
//       name: "",
//       owner_name: "",
//       phone: "",
//       state: "",
//       district: "",
//       pincode: "",
//       email: "",
//       password: "",
//       address_full: "",
//       lead_amount: "",
//       status: "new",
//       created_by_id: authUser?.id ?? "",
//       created_by_name: authUser?.name ?? "",
//       lead_type: "",
//     });

//     setErrors({});
//   } catch (error) {
//     toast.error("Something went wrong");
//   } finally {
//     setLoading(false);
//   }
// };


//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4">
//       <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
//         Create Lead
//       </h2>

//       <form
//         onSubmit={handleSubmit}
//         className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs md:text-sm"
//       >
//         {/* Name */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Name</label>
//           <input
//             name="name"
//             value={form.name}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Full Name"
//           />
//           {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
//         </div>

//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Owner Name</label>
//           <input
//             name="owner_name"
//             value={form.owner_name}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Full Name"
//           />
//           {errors.owner_name && <span className="text-red-500 text-xs">{errors.owner_name}</span>}
//         </div>

//         {/* Phone */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Phone</label>
//           <input
//             name="phone"
//             value={form.phone}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Phone Number"
//           />
//           {errors.phone && <span className="text-red-500 text-xs">{errors.phone}</span>}
//         </div>

//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Pincode</label>

//           <input
//             name="pincode"
//             type="number"
//             value={form.pincode}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Pincode"
//           />

//           {/* Error message */}
//           {errors.pincode && (
//             <span className="text-red-500 text-xs">{errors.pincode}</span>
//           )}

//           {/* Fetching loader */}
//           {pinLoading && (
//             <span className="text-blue-500 text-xs">Fetching...</span>
//           )}
//         </div>

//         {/* State */}
//         <div className="flex flex-col gap-1">
//         <label className="text-slate-600">State</label>
//         <input
//           name="state"
//           value={form.state}
//           readOnly
//           className="border border-slate-200 bg-gray-100 rounded-lg px-2 py-1.5"
//           placeholder="State"
//         />
//         {errors.state && <span className="text-red-500 text-xs">{errors.state}</span>}
//       </div>


//         {/* District */}
//        <div className="flex flex-col gap-1">
//           <label className="text-slate-600">District</label>
//           <input
//             name="district"
//             value={form.district}
//             readOnly
//             className="border border-slate-200 bg-gray-100 rounded-lg px-2 py-1.5"
//             placeholder="District"
//           />
//           {errors.district && (
//             <span className="text-red-500 text-xs">{errors.district}</span>
//           )}
//         </div>


//         {/* Pincode */}
        


//         {/* Email */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Email</label>
//           <input
//             name="email"
//             type="email"
//             value={form.email}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Email Address"
//           />
//           {errors.email && <span className="text-red-500 text-xs">{errors.email}</span>}
//         </div>

//         {/* Password */}
//         {/* <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Password</label>
//           <input
//             name="password"
//             type="password"
//             value={form.password}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Password"
//           />
//           {errors.password && <span className="text-red-500 text-xs">{errors.password}</span>}
//         </div> */}

//         {/* Lead Amount */}
//         {/* Package Selection */}
        


//         {/* Status */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Status</label>
//           <select
//             name="status"
//             value={form.status}
//             disabled
//             className="border border-slate-200 rounded-lg px-2 py-1 bg-gray-100 cursor-not-allowed"
//           >
//             <option value={form.status}>
//               {STATUSES.find((s) => s.value === form.status)?.label || "New"}
//             </option>
//           </select>
//         </div>

//         {/* Lead For (Role Dropdown) */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Lead For</label>
//           <select
//             name="lead_type"
//             value={form.lead_type}
//             onChange={handleChange}
//             disabled={filteredRoleOptions().length === 1}
//             className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
//           >
//             <option value="">Select Type</option>
//             {filteredRoleOptions().map((opt) => (
//               <option key={opt} value={opt}>
//                 {opt.charAt(0).toUpperCase() + opt.slice(1)}
//               </option>
//             ))}
//           </select>
//           {errors.lead_type && (
//             <span className="text-red-500 text-xs">{errors.lead_type}</span>
//           )}
//         </div>

//         <div className="flex flex-col gap-1 md:col-span-2">
//           <label className="text-slate-600">Package</label>

//           {form.package_name ? (
//             <div className="w-full border border-blue-300 bg-blue-50 px-3 py-2 rounded-lg flex justify-between">
//               <div>
//                 <p className="font-semibold">{form.package_name}</p>
//                 <p className="w-full text-xs text-gray-600">
//                   ₹{form.lead_amount} • {form.validity_days} days • {form.badge_name} • {form.benefits}% Extra
//                 </p>
//               </div>

//               <button
//                 type="button"
//                 className="text-red-600 text-xs"
//                 onClick={() =>
//                   setForm((prev) => ({
//                     ...prev,
//                     lead_amount: "",
//                     package_name: "",
//                     validity_days: "",
//                     badge_name: "",
//                     benefits: "",
//                   }))
//                 }
//               >
//                 Remove
//               </button>
//             </div>
//           ) : (
//             <button
//               type="button"
//               onClick={fetchPackages}
//               className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
//             >
//               Select Package
//             </button>
//           )}

//           {errors.lead_amount && (
//             <span className="text-red-500 text-xs">{errors.lead_amount}</span>
//           )}
//         </div>


//         <div className="flex flex-col gap-1 md:col-span-2">
//           <label className="text-slate-600">Full Address</label>
//           <textarea
//             name="address_full"
//             value={form.address_full}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5 h-20"
//             placeholder="Complete Address"
//           />
//           {errors.address_full && (
//             <span className="text-red-500 text-xs">{errors.address_full}</span>
//           )}
//         </div>

//         {/* Submit Button */}
//         <button
//           type="submit"
//           disabled={loading}
//           className="md:col-span-2 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-sm"
//         >
//           {loading ? "Saving..." : "Save Lead"}
//         </button>
//       </form>


//       {/* PACKAGE MODAL */}
//       {packageModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
//             <div className="bg-white w-full max-w-xl rounded-lg shadow-lg p-4">
//               <h3 className="text-lg font-semibold mb-3">Choose a Package</h3>

//               {packageLoading ? (
//                 <RegularLoader/>
//               ) : (
//                 <div className="grid grid-cols-2 gap-3 max-h-90 overflow-y-auto">
//                   {packages.map((pkg) => (
//                     <div
//                       key={pkg.id}
//                       onClick={() => handleSelectPackage(pkg)}
//                       className="cursor-pointer border border-gray-300 rounded-xl p-4 hover:border-blue-500 hover:bg-blue-50 transition duration-200 shadow-sm"
//                     >
//                       {/* Package Title */}
//                       <h4 className="text-lg font-semibold text-gray-800">
//                         {pkg.package_name} (₹{pkg.amount}/{pkg.validity_days} Days)
//                       </h4>

//                       {/* Amount & Validity
//                       <div className="mt-1 text-sm text-gray-700">
//                         <p><span className="font-semibold">Amount: </span></p>
//                         <p><span className="font-semibold">Validity: </span></p>
//                       </div> */}

//                       {/* Badge Section */}
//                       {pkg.badge && (
//                         <div className="mt-3 p-3 rounded-lg border border-gray-200">
                          
//                           {/* Badge Name Highlight */}
//                           <span className="text-xs font-semibold bg-blue-200 text-blue-800 px-2 py-1 rounded">
//                             {pkg.badge.name}
//                           </span>

//                           <p className="text-xs text-gray-700 mt-2">
//                             {pkg.badge.description}
//                           </p>

//                           <div className="text-xs text-gray-700 mt-2 space-y-1">
//                             <p>
//                               <span className="font-semibold">Eligibility:</span> {pkg.badge.eligibility} Users
//                             </p>
//                             <p>
//                               <span className="font-semibold">Benefits:</span> {pkg.badge.benefits}% Extra
//                             </p>
//                           </div>
//                         </div>
//                       )}

//                     </div>
//                   ))}
//                 </div>
//               )}

//               <button
//                 onClick={() => setPackageModal(false)}
//                 className="w-full mt-4 py-2 text-center bg-gray-300 rounded-lg hover:bg-gray-400"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         )}


//     </div>
//   );
// }



// import React, { useState } from "react";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";

// export default function CreateLeadForm({ onCreate }) {
//   const [form, setForm] = useState({
//     name: "",
//     phone: "",
//     state: "",
//     district: "",
//     pincode: "",
//     email: "",
//     password: "",
//     address_full: "",
//     lead_amount: ,
//     status: "New",
//   });

//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);

//   const handleChange = (e) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };

//   // ---------------- VALIDATION ------------------
//   const validate = () => {
//     let newErrors = {};

//     if (!form.name.trim()) newErrors.name = "Name is required";
//     else if (form.name.trim().length < 3)
//       newErrors.name = "Name must be at least 3 characters";

//     if (!form.phone.trim()) newErrors.phone = "Phone is required";
//     else if (!/^[0-9]{10}$/.test(form.phone))
//       newErrors.phone = "Phone must be 10 digits";

//     if (!form.state.trim()) newErrors.state = "State is required";

//     if (!form.district.trim()) newErrors.district = "District is required";

//     if (!/^[0-9]{6}$/.test(form.pincode))
//       newErrors.pincode = "Pincode must be 6 digits";

//     if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
//       newErrors.email = "Invalid email format";

//     if (!form.password.trim()) newErrors.password = "Password is required";
//     else if (form.password.length < 6)
//       newErrors.password = "Password must be at least 6 characters";

//     if (!form.address_full.trim())
//       newErrors.address_full = "Address cannot be empty";

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ---------------- SUBMIT ------------------
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validate()) return; // ❌ Stop submit if validation fails

//     setLoading(true);

//     try {
//       const response = await fetch(
//         "${BASE_URL}/warranty/wlead/store",
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(form),
//         }
//       );

//       const data = await response.json();

//       if (!response.ok) {
//         alert(data?.message || "Failed to create lead");
//         return;
//       }

//       onCreate && onCreate(data);

//       alert("Lead created successfully!");

//       setForm({
//         name: "",
//         phone: "",
//         state: "",
//         district: "",
//         pincode: "",
//         email: "",
//         password: "",
//         address_full: "",
//         status: "New",
//       });
//       setErrors({});
//     } catch (error) {
//       alert("Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ---------------- UI ------------------
//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4">
//       <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
//         Create Lead
//       </h2>

//       <form
//         onSubmit={handleSubmit}
//         className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs md:text-sm"
//       >
//         {/* Name */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Name</label>
//           <input
//             name="name"
//             value={form.name}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Full Name"
//           />
//           {errors.name && (
//             <span className="text-red-500 text-xs">{errors.name}</span>
//           )}
//         </div>

//         {/* Phone */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Phone</label>
//           <input
//             name="phone"
//             value={form.phone}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Phone Number"
//           />
//           {errors.phone && (
//             <span className="text-red-500 text-xs">{errors.phone}</span>
//           )}
//         </div>

//         {/* State */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">State</label>
//           <input
//             name="state"
//             value={form.state}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="State Name"
//           />
//           {errors.state && (
//             <span className="text-red-500 text-xs">{errors.state}</span>
//           )}
//         </div>

//         {/* District */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">District</label>
//           <input
//             name="district"
//             value={form.district}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="District Name"
//           />
//           {errors.district && (
//             <span className="text-red-500 text-xs">{errors.district}</span>
//           )}
//         </div>

//         {/* Pincode */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Pincode</label>
//           <input
//             name="pincode"
//             type="number"
//             value={form.pincode}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Pincode"
//           />
//           {errors.pincode && (
//             <span className="text-red-500 text-xs">{errors.pincode}</span>
//           )}
//         </div>

//         {/* Email */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Email</label>
//           <input
//             name="email"
//             type="email"
//             value={form.email}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Email Address"
//           />
//           {errors.email && (
//             <span className="text-red-500 text-xs">{errors.email}</span>
//           )}
//         </div>

//         {/* Password */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Password</label>
//           <input
//             name="password"
//             type="password"
//             value={form.password}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5"
//             placeholder="Password"
//           />
//           {errors.password && (
//             <span className="text-red-500 text-xs">{errors.password}</span>
//           )}
//         </div>

//         {/* Status */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600">Status</label>
//           <select
//             name="status"
//             value={form.status}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
//           >
//             {STATUSES.map((s) => (
//               <option key={s} value={s}>
//                 {s}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Address Full */}
//         <div className="flex flex-col gap-1 md:col-span-2">
//           <label className="text-slate-600">Full Address</label>
//           <textarea
//             name="address_full"
//             value={form.address_full}
//             onChange={handleChange}
//             className="border border-slate-200 rounded-lg px-2 py-1.5 h-20"
//             placeholder="Complete Address"
//           ></textarea>
//           {errors.address_full && (
//             <span className="text-red-500 text-xs">
//               {errors.address_full}
//             </span>
//           )}
//         </div>

//         {/* Submit */}
//         <button
//           type="submit"
//           disabled={loading}
//           className="md:col-span-2 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-sm"
//         >
//           {loading ? "Saving..." : "Save Lead"}
//         </button>
//       </form>
//     </div>
//   );
// }




// import react, { useState } from "react";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";

// export default function CreateLeadForm({ onCreate }) {
//   const [name, setName] = useState("");
//   const [owner, setOwner] = useState("");
//   const [status, setStatus] = useState("New");
//   const [value, setValue] = useState("");

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     const numericValue = parseFloat(value) || 0;

//     if (!name.trim() || !owner.trim()) return;

//     // 🚨 Minimum value validation (₹2000)
//     if (numericValue < 2000) {
//       alert("Minimum lead value should be ₹2000");
//       return;
//     }

//     const today = new Date();
//     const createdAt = today.toISOString().slice(0, 10);

//     onCreate({
//       id: Date.now(),
//       name: name.trim(),
//       owner: owner.trim(),
//       status,
//       value: numericValue,
//       createdAt,
//     });

//     setName("");
//     setOwner("");
//     setStatus("New");
//     setValue("");
//   };

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4">
//       <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
//         Create Lead
//       </h2>

//       <form
//         onSubmit={handleSubmit}
//         className="flex flex-col gap-3 text-xs md:text-sm"
//       >
//         {/* Lead Name */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Lead Name</label>
//           <input
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
//             value={name}
//             onChange={(e) => setName(e.target.value)}
//             placeholder="e.g. New CRM rollout"
//           />
//         </div>

//         {/* Owner */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Owner</label>
//           <input
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
//             value={owner}
//             onChange={(e) => setOwner(e.target.value)}
//             placeholder="e.g. Priya Sharma"
//           />
//         </div>

//         {/* Status */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Status</label>
//           <select
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm bg-white"
//             value={status}
//             onChange={(e) => setStatus(e.target.value)}
//           >
//             {STATUSES.map((s) => (
//               <option key={s} value={s}>
//                 {s}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Value (₹) */}
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Value (₹)</label>
//           <input
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
//             type="number"
//             min="2000"                    // 🔥 HTML minimum
//             value={value}
//             onChange={(e) => setValue(e.target.value)}
//             placeholder="Minimum value ₹2000"
//           />
//         </div>

//         {/* Save Button */}
//         <button
//           type="submit"
//           className="mt-1 inline-flex items-center justify-center rounded-lg bg-blue-600 text-white text-xs md:text-sm font-medium py-1.5 px-3 hover:bg-blue-700 transition-colors"
//         >
//           Save Lead
//         </button>
//       </form>
//     </div>
//   );
// }


// import react, { useState } from "react";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// export default function CreateLeadForm({ onCreate }) {
//   const [name, setName] = useState("");
//   const [owner, setOwner] = useState("");
//   const [status, setStatus] = useState("New");
//   const [value, setValue] = useState("");

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     const numericValue = parseFloat(value) || 0;
//     if (!name.trim() || !owner.trim() || numericValue <= 0) return;

//     const today = new Date();
//     const createdAt = today.toISOString().slice(0, 10);

//     onCreate({
//       id: Date.now(),
//       name: name.trim(),
//       owner: owner.trim(),
//       status,
//       value: numericValue,
//       createdAt,
//     });

//     setName("");
//     setOwner("");
//     setStatus("New");
//     setValue("");
//   };

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4">
//       <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
//         Create Lead
//       </h2>
//       <form
//         onSubmit={handleSubmit}
//         className="flex flex-col gap-3 text-xs md:text-sm"
//       >
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Lead Name</label>
//           <input
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
//             value={name}
//             onChange={(e) => setName(e.target.value)}
//             placeholder="e.g. New CRM rollout"
//           />
//         </div>
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Owner</label>
//           <input
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
//             value={owner}
//             onChange={(e) => setOwner(e.target.value)}
//             placeholder="e.g. Priya Sharma"
//           />
//         </div>
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Status</label>
//           <select
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm bg-white"
//             value={status}
//             onChange={(e) => setStatus(e.target.value)}
//           >
//             {STATUSES.map((s) => (
//               <option key={s} value={s}>
//                 {s}
//               </option>
//             ))}
//           </select>
//         </div>
//         <div className="flex flex-col gap-1">
//           <label className="text-slate-600 text-xs">Value (₹)</label>
//           <input
//             className="border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
//             type="number"
//             min="0"
//             value={value}
//             onChange={(e) => setValue(e.target.value)}
//             placeholder="e.g. 50000"
//           />
//         </div>
//         <button
//           type="submit"
//           className="mt-1 inline-flex items-center justify-center rounded-lg bg-blue-600 text-white text-xs md:text-sm font-medium py-1.5 px-3 hover:bg-blue-700 transition-colors"
//         >
//           Save Lead
//         </button>
//       </form>
//     </div>
//   );
// }